import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { PROJECTILE_HEIGHT } from "@arena/shared";

/** Fill Babylon's texture cache from the menu, before the first cast. */
export function preloadSummonArt(scene:Scene):void {
  for(const file of ['virgilio-cast','malebranche-charge']){
    const texture=new Texture(`/proto2d/dante-v2/${file}.png`,scene,true,true,Texture.NEAREST_SAMPLINGMODE);
    texture.hasAlpha=true;
  }
}

/** PixelLab drawing only: damage and collisions belong to server projectiles/zones. */
export class SummonView {
  private readonly mesh: Mesh;
  private readonly texture: Texture;
  private readonly material: StandardMaterial;
  private age = 0;
  private readonly right = new Vector3();
  private readonly forward = new Vector3();
  private readonly scene: Scene;
  private readonly kind: "malebranche" | "virgilio";
  private readonly drawn=new Vector3();
  private positioned=false;

  constructor(scene: Scene, kind: "malebranche" | "virgilio") {
    this.scene = scene;
    this.kind = kind;
    this.mesh = CreatePlane(`summon-${kind}`, { size: 1 }, scene);
    this.mesh.billboardMode = Mesh.BILLBOARDMODE_Y;
    this.mesh.isPickable = false;
    this.texture = new Texture(`/proto2d/dante-v2/${kind === "virgilio" ? "virgilio-cast" : "malebranche-charge"}.png`, scene, true, true, Texture.NEAREST_SAMPLINGMODE);
    this.texture.hasAlpha = true;
    this.texture.wrapU = this.texture.wrapV = Texture.CLAMP_ADDRESSMODE;
    this.material = new StandardMaterial(`summon-${kind}`, scene);
    this.material.diffuseTexture = this.texture;
    this.material.useAlphaFromDiffuseTexture = true;
    this.material.emissiveColor = new Color3(0.85, 0.8, 0.76);
    this.material.specularColor = Color3.Black();
    this.material.backFaceCulling = false;
    this.mesh.material = this.material;
  }

  update(x: number, y: number, z: number, fx: number, fz: number, dt: number): void {
    this.age += dt;
    const camera = this.scene.activeCamera;
    if (!camera) return;
    camera.getDirectionToRef(Vector3.Right(), this.right);
    camera.getDirectionToRef(Vector3.Forward(), this.forward);
    const flipped = fx * this.right.x + fz * this.right.z < 0;
    const tilt = Math.max(0.55, Math.hypot(this.forward.x, this.forward.z));
    const size = this.kind === "malebranche" ? 2.8 : 2.5;
    this.mesh.scaling.set(size, size / tilt, 1);
    if(!this.positioned){this.drawn.set(x,y,z);this.positioned=true;}
    const follow=this.kind==='malebranche'?1-Math.exp(-dt*30):1;
    this.drawn.x+=(x-this.drawn.x)*follow;this.drawn.y+=(y-this.drawn.y)*follow;this.drawn.z+=(z-this.drawn.z)*follow;
    this.mesh.position.set(this.drawn.x, this.drawn.y - (this.kind === "malebranche" ? PROJECTILE_HEIGHT : 0) + size / (2 * tilt) - 0.12, this.drawn.z);
    const frame = this.kind === "virgilio" ? Math.min(11, Math.floor(this.age * 10)) : 4 + Math.floor(this.age * 20) % 8;
    this.texture.uScale = (flipped ? -1 : 1) / 4;
    this.texture.vScale = 1 / 3;
    this.texture.uOffset = ((frame % 4) + (flipped ? 1 : 0)) / 4;
    this.texture.vOffset = 1 - (Math.floor(frame / 4) + 1) / 3;
    this.material.alpha = this.kind==='malebranche'?1:Math.min(1, this.age / 0.08);
    if (this.kind === "virgilio") this.material.alpha *= Math.min(1, Math.max(0, (1.8 - this.age) / 0.3));
  }

  dispose(): void { this.mesh.dispose(false, true); }
}
